import React from "react";
import { Modal, Box, Typography } from "@mui/material";

import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../components/Input";
import Button from "../components/Button";
import { generateEncryptedKeyBody } from "../utils/crypto";
import { CommonBody } from "../types/General";
import { useChangePasswordMutation } from "../service/auth";
import { errorToast, successToast } from "../helpers";

export const ChangePasswordModal = ({ open, handleClose }: any) => {
  const [changePassword] = useChangePasswordMutation();

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old password is required."),
      newPassword: Yup.string()
        .required("Old password is required.")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/,
          "Password must be at least 8 characters long and include 1 uppercase, 1 lowercase, and 1 special character."
        )
        .notOneOf(
          [Yup.ref("oldPassword")],
          "New password must be different from old password."
        )
        .required("New password is required."),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match.")
        .required("Confirm password is required."),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);
      const body = {
        old_password: formik.values.oldPassword,
        new_password: formik.values.confirmPassword,
      };

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await changePassword(encryptedBody).unwrap();
        if (response?.status === 200) {
          formik.resetForm();
          successToast("Password changed successfully");
          handleClose();
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "Something went wrong");
      }
      formik.setSubmitting(false);
    },
  });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      onBackdropClick={() => formik.resetForm()}
    >
      <Box
        sx={{
          width: 400,
          bgcolor: "background.paper",
          p: 3,
          borderRadius: 2,
          boxShadow: 24,
          margin: "auto",
          mt: "10%",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Change Password
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Input
            placeholder="Old Password"
            password
            id="oldPassword"
            name="oldPassword"
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.oldPassword && formik.errors.oldPassword}
          />
          <Input
            placeholder="New Password"
            password
            id="newPassword"
            name="newPassword"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <Input
            placeholder="Confirm Password"
            password
            id="confirmPassword"
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
          />
          <Button value="Update Password" type="submit" />
        </form>
      </Box>
    </Modal>
  );
};
